"use client";
import { useMemo, useState } from "react";
import type { KeyMetricsExplorerComponent } from "../../types";
import { Search } from "lucide-react";
import { useLocale } from "../../runtime/RendererRuntime";
import { t } from "../../i18n/strings";

type Metric = { id: string; category: string; label: string; value: string; unit?: string };

/** Key metrics explorer renderer with search and category filtering. */
export function KeyMetricsExplorerRenderer({ component }: { component: KeyMetricsExplorerComponent }) {
  const locale = useLocale();
  const content = component.content ?? {};
  const METRICS: Metric[] = (content as any).metrics ?? [];
  const defaultCategory = (content as any).defaultCategory ?? "all";
  const searchPlaceholder = (content as any).searchPlaceholder ?? t(locale, "searchIndicator");

  const [q, setQ] = useState("");
  const [category, setCategory] = useState<string>(defaultCategory);

  const categories = useMemo(() => ["all", ...Array.from(new Set(METRICS.map((m) => m.category)))], []);
  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    return METRICS.filter((m) => {
      const matchCat = category === "all" || m.category === category;
      const matchText = !term || m.label.toLowerCase().includes(term) || m.category.toLowerCase().includes(term);
      return matchCat && matchText;
    });
  }, [q, category]);

  return (
    <>
      <section className="mb-6 grid gap-3 md:mb-8 md:grid-cols-[1fr,220px]">
        <div className="relative">
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="h-10 w-full rounded-md border border-slate-300 pl-10 pr-3 text-sm xl:text-base outline-none focus:border-[#00A356]"
            placeholder={searchPlaceholder}
            aria-label={t(locale, "search")}
          />
          <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
        </div>
        <div>
          <label className="sr-only" htmlFor="category">Catégorie</label>
          <select
            id="category"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm xl:text-base outline-none focus:border-[#00A356]"
          >
            {categories.map((c) => (
              <option key={c} value={c}>{c === "all" ? t(locale, "allCategories") : c}</option>
            ))}
          </select>
        </div>
      </section>

      <section className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {filtered.map((m) => (
          <article key={m.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm ring-1 ring-black/5">
            <div className="text-xs xl:text-sm uppercase tracking-wide text-slate-500">{m.category}</div>
            <h2 className="mt-1 text-[15px] xl:text-[17px] font-medium text-[#121212]">{m.label}</h2>
            <div className="mt-3 text-2xl font-semibold text-[#00A356]">{m.value}{m.unit ? ` ${m.unit}` : ""}</div>
          </article>
        ))}
        {filtered.length === 0 && (
          <div className="col-span-full rounded-md border border-dashed p-6 text-center text-slate-500">{t(locale, "noResults")}</div>
        )}
      </section>
    </>
  );
}


